import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { formatDateLocale } from '../../utils/dateFormat';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, Plus, ArrowRight, FileText, CreditCard, CheckCircle2, Clock, Send } from 'lucide-react';

const STATUS_COLORS = {
  NEW: 'bg-blue-100 text-blue-800',
  OFFER_SENT: 'bg-amber-100 text-amber-800',
  CONVERTED: 'bg-green-100 text-green-800',
  offered: 'bg-amber-100 text-amber-800',
  ACCEPTED: 'bg-green-100 text-green-800',
  SENT: 'bg-blue-100 text-blue-800',
  pending: 'bg-yellow-100 text-yellow-800',
  initiated: 'bg-blue-100 text-blue-800',
};

export default function PatientJourney() {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadDetail, setLeadDetail] = useState(null);
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [form, setForm] = useState({ treatment_type: '', preferred_country: '', notes: '' });

  const headers = {};

  const fetchLeads = useCallback(async () => {
    try {
      const res = await api.get('/patient/leads', { headers });
      setLeads(res.data?.leads || []);
    } catch {
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const createLead = async (e) => {
    e.preventDefault();
    if (!form.treatment_type.trim()) return;
    setCreating(true);
    try {
      const res = await api.post('/patient/leads', form, { headers });
      if (res.data?.success) {
        setLeads(prev => [res.data.lead, ...prev]);
        setForm({ treatment_type: '', preferred_country: '', notes: '' });
        setShowForm(false);
      }
    } catch (err) {
      console.error('Create lead failed:', err);
    } finally {
      setCreating(false);
    }
  };

  const viewLeadDetail = async (leadId) => {
    try {
      const res = await api.get(`/patient/leads/${leadId}`, { headers });
      if (res.data?.success) {
        setLeadDetail(res.data);
        setSelectedLead(leadId);
        setPaymentIntent(null);
      }
    } catch (err) {
      console.error('Fetch lead detail failed:', err);
    }
  };

  const acceptOffer = async (offerId) => {
    try {
      const res = await api.post(`/patient/quotes/${offerId}/accept`, {}, { headers });
      if (res.data?.success) {
        viewLeadDetail(selectedLead);
      }
    } catch (err) {
      console.error('Accept failed:', err);
    }
  };

  const viewPaymentIntent = async (contractId) => {
    try {
      const res = await api.get(`/payment/intent/${contractId}`, { headers });
      if (res.data?.success) {
        setPaymentIntent(res.data);
      }
    } catch (err) {
      console.error('Payment intent failed:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="journey-loading">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="patient-journey-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="journey-title">{t('patient.journey.title')}</h1>
          <p className="text-sm text-slate-500 mt-1">{t('patient.journey.trackSubtitle')}</p>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          data-testid="create-lead-btn"
          className="gap-2"
        >
          <Plus className="h-4 w-4" /> {t('patient.journey.newInquiry')}
        </Button>
      </div>

      {showForm && (
        <Card data-testid="create-lead-form">
          <CardHeader>
            <CardTitle className="text-lg">{t('patient.dashboard.requestQuote')}</CardTitle>
            <CardDescription>{t('patient.journey.tellUs')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={createLead} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">{t('patient.journey.treatmentTypeLabel')}</label>
                <Input
                  data-testid="lead-treatment-type"
                  placeholder={t('patient.journey.treatmentTypePh')}
                  value={form.treatment_type}
                  onChange={e => setForm(f => ({ ...f, treatment_type: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">{t('patient.travel.departureCountry')}</label>
                <Input
                  data-testid="lead-preferred-country"
                  placeholder={t('patient.journey.countryPh')}
                  value={form.preferred_country}
                  onChange={e => setForm(f => ({ ...f, preferred_country: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">{t('patient.intake.notes')}</label>
                <Textarea
                  data-testid="lead-notes"
                  placeholder={t('patient.journey.additionalDetails')}
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={3}
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={creating} data-testid="submit-lead-btn" className="gap-2">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {t('patient.journey.submitInquiry')}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>{t('patient.common.cancel')}</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Lead List */}
      <div className="space-y-3" data-testid="leads-list">
        {leads.length === 0 && !showForm && (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-medium">{t('patient.journey.empty')}</p>
              <p className="text-sm mt-1">{t('patient.journey.startHint')}</p>
            </CardContent>
          </Card>
        )}

        {leads.map(lead => (
          <Card
            key={lead.id}
            className={`cursor-pointer transition-all hover:shadow-md ${selectedLead === lead.id ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => viewLeadDetail(lead.id)}
            data-testid={`lead-card-${lead.id}`}
          >
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">{lead.treatment_display_name || lead.treatment_interest}</p>
                  <p className="text-sm text-slate-500">
                    {lead.preferred_country && `${lead.preferred_country} · `}
                    {(() => {
                      const raw = lead.created_at;
                      if (!raw) return t('patient.journey.dateUnknown');
                      const d = new Date(raw);
                      // TREATX-BATCH-B1.2 — canonical DD/MM/YYYY presentation.
                      return Number.isNaN(d.getTime())
                        ? t('patient.journey.dateUnknown')
                        : formatDateLocale(raw);
                    })()}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={STATUS_COLORS[lead.status] || 'bg-slate-100 text-slate-800'} data-testid={`lead-status-${lead.id}`}>
                    {lead.status}
                  </Badge>
                  <ArrowRight className="h-4 w-4 text-slate-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Lead Detail with Offers */}
      {leadDetail && selectedLead && (
        <Card data-testid="lead-detail-panel">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {leadDetail.lead?.treatment_display_name || leadDetail.lead?.treatment_interest}
            </CardTitle>
            <CardDescription>
              {t('patient.journey.statusPrefix')} <Badge className={STATUS_COLORS[leadDetail.lead?.status] || ''} data-testid="detail-lead-status">
                {leadDetail.lead?.status}
              </Badge>
              {' · '}{t('patient.journey.offerCount', { count: leadDetail.offer_count || 0 })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Offers */}
            {(leadDetail.offers || []).length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <Clock className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                <p>{t('patient.journey.waitingForOffers')}</p>
              </div>
            )}
            {(leadDetail.offers || []).map(offer => (
              <Card key={offer.id} className="border" data-testid={`offer-card-${offer.id}`}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{offer.treatment_name || t('patient.journey.treatmentOfferFallback')}</p>
                      <p className="text-xl font-bold text-slate-900 mt-1" data-testid={`offer-total-${offer.id}`}>
                        {/* TREATX-BATCH-B1 — Quote Consumer SSOT Closure.
                            The canonical `financial_summary.total` is
                            attached by GET /api/patient/leads/{lead_id}
                            (backend line 187, patient_journey_routes.py).
                            Never fall back to `offer.base_price` as a
                            headline price — Owner UAT proved this leaks
                            €4,444 (base only) while canonical is €15,455. */}
                        {(() => {
                          const fs = offer.financial_summary || {};
                          const cur = fs.currency || offer.currency || '';
                          const total = Number.isFinite(fs.total) ? fs.total : null;
                          return total != null
                            ? `${cur} ${total.toLocaleString()}`
                            : '—';
                        })()}
                      </p>
                      {offer.notes_patient_visible && (
                        <p className="text-sm text-slate-500 mt-1">{offer.notes_patient_visible}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={STATUS_COLORS[offer.status] || ''} data-testid={`offer-status-${offer.id}`}>
                        {offer.status}
                      </Badge>
                      {offer.status === 'SENT' && (
                        <Button
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); acceptOffer(offer.id); }}
                          data-testid={`accept-offer-${offer.id}`}
                          className="gap-1"
                        >
                          <CheckCircle2 className="h-4 w-4" /> {t('patient.journey.acceptOffer')}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Financial Contract + Payment */}
            {leadDetail.financial_contract && (
              <Card className="border-green-200 bg-green-50" data-testid="contract-section">
                <CardContent className="py-4">
                  <h3 className="font-medium text-green-900 flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5" /> {t('patient.journey.contractLocked')}
                  </h3>
                  <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">{t('patient.offers.totalPrice')}</span>
                      <p className="font-bold" data-testid="contract-gross">
                        {leadDetail.financial_contract.currency} {leadDetail.financial_contract.gross_amount_reference?.toLocaleString()}
                      </p>
                    </div>
                    {/* FE-WIRING-2 / R-7: platform-fee (commission_amount),
                        provider-net (net_transfer_to_provider) and channel
                        routing are FORBIDDEN patient-facing surfaces and
                        have been removed from this canonical-contract
                        visibility block. */}
                  </div>
                  {!paymentIntent && (
                    <Button
                      className="mt-4 gap-2"
                      onClick={() => viewPaymentIntent(leadDetail.financial_contract.contract_id)}
                      data-testid="view-payment-btn"
                    >
                      <CreditCard className="h-4 w-4" /> {t('patient.journey.viewPaymentDetails')}
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Payment Intent */}
            {paymentIntent && (
              <Card className="border-blue-200 bg-blue-50" data-testid="payment-intent-section">
                <CardContent className="py-4">
                  <h3 className="font-medium text-blue-900 flex items-center gap-2">
                    <CreditCard className="h-5 w-5" /> {t('patient.journey.paymentDetails')}
                  </h3>
                  <div className="mt-3 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">{t('patient.journey.paymentId')}</span>
                      <span className="font-mono text-xs" data-testid="payment-id">{paymentIntent.payment?.payment_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">{t('patient.journey.amountLabel')}</span>
                      <span className="font-bold" data-testid="payment-gross">
                        {paymentIntent.payment?.currency} {paymentIntent.payment?.gross_amount?.toLocaleString()}
                      </span>
                    </div>
                    {/* FE-WIRING-2 / R-7: commission_amount and
                        net_transfer_to_provider are FORBIDDEN
                        patient-facing surfaces and have been removed
                        from this canonical-payment visibility block. */}
                    <div className="flex justify-between">
                      <span className="text-slate-600">{t('patient.journey.statusLabel')}</span>
                      <Badge className={STATUS_COLORS[paymentIntent.payment?.status] || ''} data-testid="payment-status">
                        {paymentIntent.payment?.status}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
